//
import type { AxiosCollection, AxiosErrors } from '@/types/Components/Response'
import type { CompanyAddressRequest } from '@/types/Models/Company'
import type { Address } from '@/types/Models/Customer'
import type { SenderAddressesStoreState } from '@/types/Store'
import { defineStore } from 'pinia'
import { none } from '@/services/Helpers'
import { useCustomersStore } from '@/store/customers'
import CompanyProxy from '@/proxies/CompanyProxy'
import SenderAddressProxy from '@/proxies/Customer/SenderAddressProxy'
import SenderAddressTransformer from '@/transformers/SenderAddressTransformer'

export const useCustomersSenderAddressesStore = defineStore('customers/senderAddresses', {
  state: (): SenderAddressesStoreState => ({
    senderAddresses: [],
    selectedSenderAddress: null,
  }),
  getters: {
    selectedSenderAddressValue: (state: SenderAddressesStoreState) =>
      state.selectedSenderAddress ? JSON.parse(JSON.stringify(state.selectedSenderAddress)) : null,
  },
  actions: {
    resetSenderAddresses() {
      this.senderAddresses = []
    },
    async store({ companyId, data }: { companyId: string; data: any }) {
      return new SenderAddressProxy(companyId).create(data)
    },
    fetch(companyId: string) {
      return new SenderAddressProxy(companyId)
        .all()
        .then((response: AxiosCollection<Address>) => {
          this.senderAddresses = SenderAddressTransformer.fetchCollection(response.data)
          const customer = useCustomersStore().listCustomers?.find((item) => item.id === companyId)
          if (customer) {
            new CompanyProxy({ include: 'senderAddress' }).find(companyId).then((result) => {
              if (result.data) customer.senderAddress = result.data.senderAddress
            }).catch(none)
          }
          return response
        })
        .catch(none)
    },
    reset() {
      this.resetSenderAddresses()
    },
    selectSenderAddress({ companyId, addressId }: CompanyAddressRequest) {
      return new Promise((resolve, reject) => {
        new SenderAddressProxy(companyId)
          .getSenderAddress(addressId)
          .then((res) => {
            this.selectedSenderAddress = res.data
            resolve(res)
          })
          .catch((e: AxiosErrors) => reject(e))
      })
    },
    updateSenderAddress({ companyId, addressId, sender }: CompanyAddressRequest) {
      return new Promise((resolve, reject) => {
        new SenderAddressProxy(companyId)
          .update(addressId, sender)
          .then((res) => resolve(res))
          .catch((e: AxiosErrors) => reject(e))
      })
    },
    destroySenderAddress({ companyId, addressId }: CompanyAddressRequest) {
      return new Promise((resolve, reject) => {
        new SenderAddressProxy(companyId)
          .destroy(addressId)
          .then(() => resolve(null))
          .catch((e: AxiosErrors) => reject(e))
      })
    },
    setAsDefaultSenderAddress({ companyId, addressId }: CompanyAddressRequest) {
      return new Promise((resolve, reject) => {
        new SenderAddressProxy(companyId)
          .setDefaultSenderAddress(addressId)
          .then(() => resolve(null))
          .catch((e: AxiosErrors) => reject(e))
      })
    },
  },
})

export type CustomersSenderAddressesStore = ReturnType<typeof useCustomersSenderAddressesStore>
